import { default as moment } from "moment";
import { Editor } from "./Editor.js";
import * as dtv from "../../data/values.js";
import { i18n } from "../../globalization/i18n.js";

const DROPDOWN_STYLES_MAP = {
    'top-left': '.top.left',
    'top-center': '.top',
    'top-right': '.top.right',
    'bottom-left': '.bottom.left',
    'bottom-center': '.bottom',
    'bottom-right': '.bottom.right',
};

let $instanceCounter = 0;

function appendUniqueFormat(target, format) {

    if (format === undefined || format === null)
        return;
    if (target.indexOf(format) >= 0)
        return;
    target.push(format);

}

function buildDerivedParseFormats(formats) {

    const variants = [];

    for (const format of (Array.isArray(formats) ? formats : [formats])) {
        if (!format) {
            appendUniqueFormat(variants, format);
            continue;
        }

        appendUniqueFormat(variants, format);

        if (typeof format != 'string')
            continue;

        const compactFormat = compactFormatSpacing(format);
        const unpaddedFormat = loosenDatePadding(format);
        const compactUnpaddedFormat = compactFormatSpacing(unpaddedFormat);

        appendUniqueFormat(variants, compactFormat);
        appendUniqueFormat(variants, unpaddedFormat);
        appendUniqueFormat(variants, compactUnpaddedFormat);
    }

    return variants;

}

function compactFormatSpacing(format) {

    if (typeof format != 'string')
        return format;

    return format
        .replace(/\s*([./-])\s*/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();

}

function loosenDatePadding(format) {

    if (typeof format != 'string')
        return format;

    let normalized = '';

    for (let i = 0; i < format.length; i++) {
        const char = format[i];

        if (char === '\\') {
            normalized += char;
            if (i + 1 < format.length) {
                normalized += format[i + 1];
                i++;
            }
            continue;
        }

        if (char === '[') {
            let j = i;
            while (j < format.length) {
                normalized += format[j];
                if (format[j] === ']')
                    break;
                j++;
            }
            i = j;
            continue;
        }

        if (char === 'D' || char === 'M') {
            let j = i;
            while (j + 1 < format.length && format[j + 1] === char)
                j++;

            const tokenLength = j - i + 1;

            if (char === 'D' && tokenLength === 2)
                normalized += 'D';
            else if (char === 'M' && tokenLength === 2)
                normalized += 'M';
            else
                normalized += char.repeat(tokenLength);

            i = j;
            continue;
        }

        normalized += char;
    }

    return normalized;

}

export class DatetimePicker extends Editor {

    constructor(parent, options) {

        super(parent, options);

        this.$id = ++$instanceCounter;

        const {
            value = null,
            valueDefault = () => this.getNewDate(),
            valueFrom = null,
            valueTo = null,
            timeZone = this.timeZone,
            locale = this.locale,
            format = 'l LT',
            extraFormats = ['l'],
            useStrictFormats = false,
            inline = false,
            stepping = 1,
            autofocus = false,
            autoclose = false,
            dropdownPosition = 'bottom-left',
            style = null,
            inputCss = null,
            inputStyle = null,
            buttonCss = null,
            buttonStyle = null,
            dropDownCss = null,
            dropDownStyle = null,
        } = options;

        this.value = value;
        this.valueDefault = valueDefault;
        this.valueFrom = valueFrom;
        this.valueTo = valueTo;
        this.timeZone = timeZone;
        this.locale = locale;
        this.format = format ?? 'l LT';
        this.extraFormats = extraFormats ?? ['l'];
        this.useStrictFormats = useStrictFormats;
        this.inline = inline;
        this.stepping = stepping;
        this.autofocus = autofocus;
        this.autoclose = autoclose;
        this.dropdownPosition = dropdownPosition;
        this.style = style;
        this.inputCss = inputCss;
        this.inputStyle = inputStyle;
        this.buttonCss = buttonCss;
        this.buttonStyle = buttonStyle;
        this.dropDownCss = dropDownCss;
        this.dropDownStyle = dropDownStyle;

        if (typeof this.valueDefault != 'function')
            this.valueDefault = this.normalizeDate(this.valueDefault, true);

        // value in selector - must not be null
        this.displayedDate = this.getValueOrDefault().clone();

        this.stepping = stepping;
        this.inline = inline;

        let actualValue = this.getValueOrDefault();

        this.format = normalizeFormat(this.format, actualValue.localeData());
        this.updateUsage();

        const explicitParseFormats = [this.format];

        (this.extraFormats || [])
            .map(format => normalizeFormat(format, actualValue.localeData()))
            .forEach((format) => {
                if (explicitParseFormats.indexOf(format) < 0)
                    explicitParseFormats.push(format);
            });

        this.parseFormats = buildDerivedParseFormats(explicitParseFormats);

        // .length > 1 protože už tam mám this.format!
        this.timeToggleAllowed = explicitParseFormats.length > 1
            && explicitParseFormats.some((format) => isPartEnabled(format, 'h') || isPartEnabled(format, 'm') || isPartEnabled(format, 's'));
        this.dateToggleAllowed = explicitParseFormats.length > 1
            && explicitParseFormats.some((format) => isPartEnabled(format, 'y') || isPartEnabled(format, 'M') || isPartEnabled(format, 'd'));

        // pro automatické konverze ze stringu, co přišel v JSONu
        this.parseFormats.push(moment.ISO_8601);
        this.useStrictFormats = useStrictFormats;

        this.months = [];
        this.years = [];
        this.weekDays = [];
        this.weeks = [];
        this.hours = [];
        this.minutes = [];

        this.$calendar = null;
        this.$editor = null;
        this.$menu = null;
        this.$outsideClickListener = null;
        this.parseError = null;
        this.rawInputText = null;

        this.refresh(true);

        return this;

        function normalizeFormat(format, localeData) {

            let newFormat = format.replace(/(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g, function (formatInput) {
                var newinput = localeData.longDateFormat(formatInput) || formatInput;
                return newinput.replace(/(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g, function (formatInput2) {
                    return localeData.longDateFormat(formatInput2) || formatInput2;
                });
            });

            return newFormat;

        }

    }

    // ------------------------------------------------------------------------


    isAutofocus() {

        return (typeof this.autofocus === 'function') ? this.autofocus.call(this) : this.autofocus;

    };

    getStyle() {

        return (typeof this.style === 'function') ? this.style.call(this) : this.style;

    };

    getInputCss() {

        return (typeof this.inputCss === 'function') ? this.inputCss.call(this) : this.inputCss;

    };

    getInputStyle() {

        return (typeof this.inputStyle === 'function') ? this.inputStyle.call(this) : this.inputStyle;

    };

    getButtonCss() {

        return (typeof this.buttonCss === 'function') ? this.buttonCss.call(this) : this.buttonCss;

    };

    getButtonStyle() {

        return (typeof this.buttonStyle === 'function') ? this.buttonStyle.call(this) : this.buttonStyle;

    };

    getDropDownCss() {

        return (typeof this.dropDownCss === 'function') ? this.dropDownCss.call(this) : this.dropDownCss;

    };

    getDropDownStyle() {

        return (typeof this.dropDownStyle === 'function') ? this.dropDownStyle.call(this) : this.dropDownStyle;

    };

    getErrors() {

        const errors = [];

        if (this.parseError)
            errors.push(this.parseError);

        const validationError = super.getValueError();

        if (validationError)
            errors.push(validationError);

        return errors;

    };

    hasError() {

        return this.getErrors().length > 0;

    };

    getValueError() {

        const errors = this.getErrors();
        return errors.length ? errors[0] : null;

    };

    getInputDisplayValue(formattedValue = null) {

        if (this.rawInputText !== null && this.rawInputText !== undefined)
            return this.rawInputText;

        return formattedValue;

    };

    validateValue() {

        return !this.hasError();

    };

    isValueInRange(value) {

        if (!value || !value.isValid?.())
            return false;

        const valueFrom = this.getValueFrom();
        const valueTo = this.getValueTo();

        if (valueFrom && value.isBefore(valueFrom))
            return false;
        if (valueTo && value.isAfter(valueTo))
            return false;

        return true;

    };

    // ------------------------------------------------------------------------

    isValueSame(value) {

        var granularity = this.isPartEnabled('s') ? 'second' : (this.isPartEnabled('m') ? 'minute' : (this.isPartEnabled('h') ? 'hour' : 'day'));

        if (this.isTimeZoneEnabled())
            return dtv.compareTzDatetimes(this.getTimeZone(), this.getValue(), value, granularity) === 0;
        return dtv.compareDatetimes(this.getValue(), value, granularity) === 0;

    };

    getValue() {

        return (typeof this.value === 'function') ? this.value.call(this) : this.value;

    }

    getValueOrDefault() {

        const locale = this.getLocale();
        let d = this.normalizeDate(this.getValue());
        if (d && d.isValid())
            return d;
        d = this.normalizeDate(this.getValueDefault());
        if (d && d.isValid())
            return d;
        if (this.isTimeZoneEnabled())
            return dtv.newTzDatetime(this.getTimeZone(), locale);
        return dtv.newDatetime(locale);

    };

    getValueFrom() {

        return (typeof this.valueFrom === 'function') ? this.valueFrom.call(this) : this.valueFrom;

    };

    getValueTo() {

        return (typeof this.valueTo === 'function') ? this.valueTo.call(this) : this.valueTo;

    };

    getTimeZone() {

        return (typeof this.timeZone === 'function') ? this.timeZone.call(this) : this.timeZone;

    };

    getLocale() {

        return (typeof this.locale === 'function') ? this.locale.call(this) : this.locale;

    };

    // ------------------------------------------------------------------------

    getNewDate() {

        let timeZone = this.getTimeZone(),
            locale = this.getLocale();

        if (this.isTimeZoneEnabled())
            return moment.utc().tz(timeZone).locale(locale);

        return moment().locale(locale);

    };

    /**
     * First date in month currently displayed in list of days.
     * @returns Datetime
     */
    getDisplayedDate() {

        return this.displayedDate;

    };

    getSelectedDay() {

        return this.getValueOrDefault().day();

    };

    getSelectedMonth() {

        return this.getValueOrDefault().month();

    };

    getSelectedYear() {

        return this.getValueOrDefault().year();

    };

    // ------------------------------------------------------------------------

    normalizeDate(date, required) {

        const locale = this.getLocale();
        const timeZone = this.getTimeZone();

        if (date === undefined || date === null) {
            if (!required)
                return date;
            if (this.isTimeZoneEnabled())
                return dtv.newTzDatetime(timeZone, locale);
            return dtv.newDatetime(locale);
        }
        if (moment.isDate(date) || moment.isMoment(date)) {
            if (this.isTimeZoneEnabled())
                return dtv.parseTzDatetime(timeZone, date, this.parseFormats, locale);
            return dtv.parseDatetime(date, this.parseFormats, locale);
        }
        if (this.isTimeZoneEnabled())
            return dtv.parseTzDatetime(timeZone, date, this.parseFormats, locale);
        return dtv.parseDatetime(date, this.parseFormats, locale);

    };

    isTimeZoneEnabled() {
        if (moment.tz === undefined)
            return false;
        let tz = this.getTimeZone();
        if (tz === undefined)
            return false;
        if (tz === null)
            return false;
        if (tz === '')
            return false;
        return true;
    };

    // ------------------------------------------------------------------------

    changeValue(value, forceChange, omitRedraw, omitRefresh) {

        if (!value)
            return;
        if (!value.isValid())
            return;
        if (!this.isValueInRange(value))
            return;
        this.clearParseError();
        this.clearRawInputText();
        if (this.stepping > 1) {
            let m = Math.floor(value.minutes() / this.stepping) * this.stepping;
            value.minutes(m);
        }
        value.seconds(0);
        this.displayedDate = value.clone();
        this.refresh();
        this.invalidate();
        super.changeValue(value, forceChange, omitRedraw, omitRefresh);

    };

    parseTextAndChangeSelectedValue(text) {

        const parsedInput = this.parseTextInput(text);
        let parsed = parsedInput?.value ?? null;

        if (!parsed || !parsed.isValid()) {
            this.setRawInputText(text);
            this.setParseError(this.createParseErrorMessage());
            this.invalidate();
            return;
        }
        if (parsedInput?.absolute === true)
            parsed = parsed.clone();
        else
            parsed = this.mergeParsedValueWithCurrentDate(parsed, parsedInput?.format ?? null, text);

        if (!parsed || !parsed.isValid()) {
            this.setRawInputText(text);
            this.setParseError(this.createParseErrorMessage());
            this.invalidate();
            return;
        }
        if (!this.isValueInRange(parsed)) {
            this.setRawInputText(text);
            this.setParseError(this.createOutOfRangeMessage());
            this.invalidate();
            return;
        }

        this.changeValue(parsed);

    };

    clearParseError() {

        this.parseError = null;

    };

    setRawInputText(text) {

        this.rawInputText = text ?? null;

    };

    clearRawInputText() {

        this.rawInputText = null;

    };

    setParseError(error) {

        this.parseError = error || null;

    };

    createParseErrorMessage() {

        return i18n.t('data.validations.datetime_parse.error');

    };

    createOutOfRangeMessage() {

        return i18n.t('data.validations.datetime_range.error');

    };

    parseTextInput(text) {

        const extendedTime = this.parseExtendedTimeInput(text);
        if (extendedTime)
            return extendedTime;

        const timeZone = this.getTimeZone();
        const formats = buildDerivedParseFormats(this.parseFormats);

        for (const format of formats) {
            if (!format || format === moment.ISO_8601)
                continue;

            const strictParsed = this.isTimeZoneEnabled()
                ? moment.tz(text, format, true, timeZone)
                : moment(text, format, true);

            if (strictParsed?.isValid())
                return { value: strictParsed, format };
        }

        const parsed = this.isTimeZoneEnabled()
            ? moment.tz(text, formats, timeZone)
            : moment(text, formats, this.useStrictFormats);

        return {
            value: parsed,
            format: null,
        };

    };

    parseExtendedTimeInput(text) {

        if (!this.useTime || this.useDate)
            return null;
        if (typeof text != 'string')
            return null;

        const normalizedText = text.trim();
        const match = /^(\d{1,3}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(normalizedText);

        if (!match)
            return null;

        const hours = Number(match[1]);
        const minutes = Number(match[2]);
        const seconds = match[3] === undefined ? 0 : Number(match[3]);
        const milliseconds = match[4] === undefined ? 0 : Number(match[4].padEnd(3, '0'));

        if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds) || Number.isNaN(milliseconds))
            return null;
        if (minutes > 59 || seconds > 59 || milliseconds > 999)
            return null;
        if (hours < 24)
            return null;

        const anchor = (this.getValueFrom?.() || this.getDisplayedDate?.() || this.getValueOrDefault?.());
        if (!anchor || !anchor.isValid?.())
            return null;

        return {
            value: anchor.clone().startOf('day')
                .hour(0)
                .minute(0)
                .second(0)
                .millisecond(0)
                .add(hours, 'hours')
                .add(minutes, 'minutes')
                .add(seconds, 'seconds')
                .add(milliseconds, 'milliseconds'),
            format: null,
            absolute: true,
        };

    };

    mergeParsedValueWithCurrentDate(parsed, sourceFormat = null, sourceText = null) {

        if (!parsed || !parsed.isValid())
            return parsed;

        const baseValue = this.getDisplayedDate?.() || this.getValueOrDefault();
        if (!baseValue || !baseValue.isValid())
            return parsed;

        const merged = baseValue.clone();
        const inferredDate = sourceFormat ? (
            isPartEnabled(sourceFormat, 'y')
            || isPartEnabled(sourceFormat, 'M')
            || isPartEnabled(sourceFormat, 'd')
        ) : inferTextHasDate(sourceText);
        const inferredTime = sourceFormat ? (
            isPartEnabled(sourceFormat, 'h')
            || isPartEnabled(sourceFormat, 'm')
            || isPartEnabled(sourceFormat, 's')
        ) : inferTextHasTime(sourceText);
        const applyDate = sourceFormat !== null
            ? inferredDate
            : (sourceText ? inferredDate : this.useDate);
        const applyTime = sourceFormat !== null
            ? inferredTime
            : (sourceText ? inferredTime : this.useTime);

        if (applyDate) {
            merged.year(parsed.year());
            merged.month(parsed.month());
            merged.date(parsed.date());
        }
        if (applyTime) {
            merged.hour(parsed.hour());
            merged.minute(parsed.minute());
            merged.second(parsed.second());
            merged.millisecond(parsed.millisecond());
        }

        return merged;

        function inferTextHasDate(text) {
            if (typeof text != 'string')
                return false;
            return /[./-]/.test(text.trim());
        }

        function inferTextHasTime(text) {
            if (typeof text != 'string')
                return false;
            return /:/.test(text.trim());
        }

    };

    modifyValue(offset, dimension) {

        var date = this.getDisplayedDate().clone();
        if (offset < 0)
            date.subtract(-offset, dimension);
        else
            date.subtract(-offset, dimension);
        this.changeValue(date);

    };

    modifyValueParts(data) {

        data ||= {};

        if (!this.isPartEnabled('y'))
            data.year = 0;
        if (!this.isPartEnabled('M'))
            data.month = 0;
        if (!this.isPartEnabled('d'))
            data.date = 0;
        if (!this.isPartEnabled('h'))
            data.hours = 0;
        if (!this.isPartEnabled('m'))
            data.minutes = 0;
        if (!this.isPartEnabled('s'))
            data.seconds = 0;

        var date = this.getValueOrDefault().clone();
        this.changeValue(date.set(data));

    };

    changeYear(value) {

        if (value === null) return;
        if (value === undefined) return;

        var date = this.getDisplayedDate().clone(),
            yearFrom = (this.getValueFrom() && this.getValueFrom().year()) || (date.year() - 5),
            yearTo = (this.getValueTo() && this.getValueTo().year()) || (date.year() + 5);

        value = (value < yearFrom ? yearFrom : (value > yearTo ? yearTo : value));
        this.changeValue(date.year(value));

    };

    changeMonth(value) {

        if (value === null) return;
        if (value === undefined) return;

        var date = this.getDisplayedDate().clone();
        this.changeValue(date.month(value));

    };

    changeDay(value) {

        if (value === null) return;
        if (value === undefined) return;

        var date = this.getDisplayedDate().clone();
        this.changeValue(date.date(value));

    };

    changeHour(value) {

        if (value === null) return;
        if (value === undefined) return;

        var date = this.getDisplayedDate().clone();
        this.changeValue(date.hour(value));

    };

    changeMinute(value) {

        if (value === null) return;
        if (value === undefined) return;

        var date = this.getDisplayedDate().clone();
        this.changeValue(date.minute(value));

    };

    // ------------------------------------------------------------------------

    updateUsage() {

        this.use24Hours = (this.format.toLowerCase().indexOf('a') < 1 && this.format.replace(/\[.*?\]/g, '').indexOf('h') < 1);
        this.useTime = this.isPartEnabled('h') || this.isPartEnabled('m') || this.isPartEnabled('s');
        this.useDate = this.isPartEnabled('y') || this.isPartEnabled('M') || this.isPartEnabled('d');

    };

    isPartEnabled(granularity) {

        return isPartEnabled(this.format, granularity);

    };

    // ------------------------------------------------------------------------

    refresh() {

        this.displayedDate = this.getValueOrDefault().clone();

        this.refreshYears();
        this.refreshMonths();
        this.refreshWeekDays();
        this.refreshWeeks();
        this.refreshHours();
        this.refreshMinutes();

    };

    // head year
    refreshYears() {

        var date = this.getDisplayedDate().clone(),
            valueFrom = this.getValueFrom(),
            valueTo = this.getValueTo(),
            yearFrom = date.year() - 5,
            yearTo = date.year() + 5,
            bfr = [];

        if (valueFrom)
            yearFrom = Math.max(yearFrom, valueFrom.year());
        if (valueTo)
            yearTo = Math.min(yearTo, valueTo.year());

        for (var i = yearFrom; i <= yearTo; i++) {
            var year = {
                value: i,
                text: i.toString()
            };
            bfr.push(year);
        }

        this.years = bfr;

    };

    // head month
    refreshMonths() {

        var date = this.getDisplayedDate().clone(),
            bfr = [];

        for (var i = 0; i < 12; i++) {
            var month = {
                value: i,
                text: date.month(i).format("MMMM")
            };
            bfr.push(month);
        }

        this.months = bfr;

    };

    // week header
    refreshWeekDays() {

        var date = this.getDisplayedDate().clone().startOf('week'),
            bfr = [];

        for (var i = 0; i < 7; i++) {
            bfr.push(date.format("dd"));
            date.add(1, 'days');
        }

        this.weekDays = bfr;

    };

    refreshWeeks() {

        var weeks = [], week,
            displayed = this.getDisplayedDate().clone(),
            selected = this.getValueOrDefault().clone().startOf('day'),
            firstAllowedDay = this.getValueFrom(),
            lastAllowedDay = this.getValueTo(),
            today = this.isTimeZoneEnabled()
                ? moment.tz(new Date(), displayed.tz()).startOf('day')
                : moment(new Date()).startOf('day'),
            firstDayOfMonth = displayed.clone().startOf('month').startOf('day'),
            lastDayOfMonth = displayed.clone().endOf('month').endOf('day'),
            firstDisplayedDay = firstDayOfMonth.clone().startOf('week').startOf('day'),
            lastDisplayedDay = lastDayOfMonth.clone().endOf('week').startOf('day'),
            date = firstDisplayedDay.clone(),
            lastWeek = 0;

        while (date.isSameOrBefore(lastDisplayedDay)) {

            if (lastWeek != date.week()) {
                week = {
                    value: date.week(),
                    text: date.week(),
                    days: []
                };
                weeks.push(week);
            }
            week.days.push({
                data: { year: date.year(), month: date.month(), date: date.date() },
                value: date.clone(),
                disabled: (firstAllowedDay && firstAllowedDay.isAfter(date) || lastAllowedDay && lastAllowedDay.isBefore(date)) || false,
                text: date.date(),
                isSelected: selected && date.isSame(selected),
                isToday: date.isSame(today),
                isPrevMonth: date.isBefore(firstDayOfMonth),
                isNextMonth: date.isAfter(lastDayOfMonth)
            });
            date.add(1, 'day');

        }

        this.weeks = weeks;

    };

    refreshHours() {

        this.hours = [];
        var t = this.getDisplayedDate().clone();
        for (var i = 0; i < 24; i++) {
            this.hours.push({
                value: i,
                text: t.hours(i).format(this.use24Hours ? 'H' : 'h A')
            });
        }

    };

    refreshMinutes() {

        this.minutes = [];
        var t = this.getDisplayedDate().clone(),
            stepping = this.stepping;
        for (var i = 0; i < 60; i += stepping) {
            this.minutes.push({
                value: i,
                text: t.minutes(i).format('mm')
            });
        }

    };

    // ------------------------------------------------------------------------

    toggleDates() {

        let format = pickFormat(!this.useDate, this.useTime, this.parseFormats);

        if (!format)
            return;

        this.format = format;
        this.updateUsage();
        this.invalidate();


    };

    toggleTimes() {

        let format = pickFormat(this.useDate, !this.useTime, this.parseFormats);

        if (!format)
            return;

        this.format = format;
        this.updateUsage();
        this.invalidate();

    };

    activateMenu() {

        if (!this.$menu) return;
        if (!this.$button) return;

        if (this.$menuActive) return;

        const picker = this;

        this.$menu.classList.add('active');

        let listener = (event) => {

            if (!picker.$menu) return;

            if (picker.$button?.contains(event.target))
                return;
            if (picker.$menu?.contains(event.target))
                return;

            picker.$menu.classList.remove('active');
            document.removeEventListener('click', listener);
            picker.$menuActive = false;

        };

        setTimeout(() => {
            picker.$menuActive = true;
            document.addEventListener('click', listener);
        }, 0);

    };

    // ------------------------------------------------------------------------

    dispose() {

    };

    _render(r, vm) {

        const picker = this;

        if (!picker.isVisible())
            return null;

        var value = picker.normalizeDate(picker.getValue()),
            selected = picker.getValueOrDefault(),
            displayed = picker.getDisplayedDate();

        const compareDisplayedAndSelected = picker.isTimeZoneEnabled()
            ? dtv.compareTzDatetimes(picker.getTimeZone(), selected, displayed)
            : dtv.compareDatetimes(selected, displayed);

        if (compareDisplayedAndSelected !== 0) {
            picker.refresh();
            displayed = picker.getDisplayedDate();
        }

        var id = picker.id,
            format = picker.format,
            formattedValue = (value && value.format(format)) || null,
            inputValue = picker.getInputDisplayValue(formattedValue),
            selectedMonth = displayed && displayed.month(),
            selectedHour = displayed && displayed.hour(),
            selectedMinute = displayed && displayed.minute(),
            isReadOnly = (picker.isReadOnly() && 'readonly') || null,
            isDisabled = (picker.isDisabled() && 'disabled') || null,
            isReadOnlyOrDisabled = ((picker.isReadOnly() || picker.isDisabled()) && 'disabled') || null,
            css = picker.getCss() || '',
            style = picker.getStyle() || null,
            inputCss = picker.getInputCss() || '',
            inputStyle = picker.getInputStyle() || null,
            buttonCss = picker.getButtonCss() || '.medium-round.chip',
            buttonStyle = picker.getButtonStyle() || null,
            dropDownCss = picker.getDropDownCss() || DROPDOWN_STYLES_MAP[picker.dropdownPosition] || '.bottom.left',
            dropDownStyle = picker.getDropDownStyle() || null;

        if (picker.inline)
            return renderCalendar();

        return r.group({
            css: css,
            props: {
                id: id,
                style: style,
                _hooks: {
                    didInsert: (node) => {
                        picker.$calendar = node.el;
                    },
                    willRemove: (node) => {
                        picker.$calendar = null;
                    }
                },
            },
        }, [
            r.input({
                css: inputCss,
                props: {
                    id: id + '-input',
                    value: inputValue,
                    readonly: isReadOnly,
                    disabled: isDisabled,
                    style: inputStyle,
                    //onfocusin: onInputControlFocus,
                    onblur: onInputControlBlur,
                    onchange: onInputControlChange,
                    'data-focus': (!(isDisabled || isReadOnly) && picker.isAutofocus()) ? 'auto' : null,
                    _hooks: {
                        didInsert: (node) => {
                            picker.$editor = node.el;
                        },
                        willRemove: (node) => {
                            picker.$editor = null;
                        }
                    }
                }
            }),
            r.menu({
                css: '.dropdown.no-wrap' + dropDownCss,
                props: {
                    id: id + '-menu',
                    style: dropDownStyle,
                    _hooks: {
                        didInsert: (node) => {
                            const menu = node.el;
                            picker.$menu = menu;
                        },
                        willRemove: (node) => {
                            picker.$menu = null;
                        },
                    }
                },
            }, [
                renderCalendar()
            ]),
            r.iconButton((!picker.useDate ? '#clock' : '#calendar'), {
                css: buttonCss,
                props: {
                    id: id + '-button',
                    disabled: isReadOnlyOrDisabled,
                    style: buttonStyle,
                    tabindex: -1,
                    onclick: (e) => {

                        e.stopPropagation();
                        picker.activateMenu();

                    },
                    _hooks: {
                        didInsert: (node) => {
                            picker.$button = node.el;
                        },
                        willRemove: (node) => {
                            picker.$button = null;
                        }
                    },
                },
            }),
        ]);

        function onInputControlBlur(e, node) {

            var target = e.relatedTarget,
                found = false;
            if (picker.$calendar) {
                while (target) {
                    if (target == picker.$calendar || target == picker.$editor) {
                        found = true;
                        break;
                    }
                    target = target.parentNode;
                }
            }

        };

        function onInputControlFocus(e, node) {

            var text = e.target.value;
            picker.parseTextAndChangeSelectedValue(text);

        };

        function onInputControlChange(e, node) {

            var text = e.target.value;
            picker.parseTextAndChangeSelectedValue(text);

        };

        function renderCalendar() {

            let dateEnabled = picker.useDate,
                timeEnabled = picker.useTime;

            return r.el('div', 'calendar', {
                tabindex: -1,
                readonly: picker.readOnly,
                disabled: picker.disabled,
                _hooks: {
                    didInsert: (node) => {
                        picker.$calendar = node.el;
                    },
                    willRemove: (node) => {
                        picker.$calendar = null;
                    },
                },
            }, [

                renderNav(),
                r.spacer(),
                (dateEnabled ? renderDatesNav() : null),
                (dateEnabled ? renderDates() : null),
                (timeEnabled ? renderTimes() : null),

            ]);

        };

        function renderNav() {

            return r.nav({}, [
                (
                    picker.dateToggleAllowed
                        ? r.iconButton('#calendar', { css: '.chip' + (picker.useDate ? '.active' : ''), props: { onclick: () => { picker.toggleDates(); } } })
                        : null
                ),
                (
                    picker.timeToggleAllowed
                        ? r.iconButton('#clock', { css: '.chip' + (picker.useTime ? '.active' : ''), props: { onclick: () => { picker.toggleTimes(); } } })
                        : null
                ),
                r.spacer(),
                renderCloseButton(),
            ]);

        }

        function renderDatesNav() {

            const months = (picker.months || []).reduce(function (options, item) {
                options.push({ value: item.value, label: item.text, data: item });
                return options;
            }, []);

            const years = (picker.years || []).reduce(function (options, item) {
                options.push({ value: item.value, label: item.text, data: item });
                return options;
            }, [])

            return r.group({}, [
                r.iconButton('#prior', { css: '.chip', props: { onclick: onSelectPrevMonthClick } }),
                r.comboBox(selectedMonth, { items: months, emptyNotDisplayed: true, onValueSet: onSelectedMonthChanged }),
                r.comboBox(displayed && displayed.year(), { items: years, emptyNotDisplayed: true, onValueSet: onSelectedYearChanged }),
                r.iconButton('#next', { css: '.chip', props: { onclick: onSelectNextMonthClick } }),
            ]);

            function onSelectPrevMonthClick(e) {
                picker.modifyValue(-1, 'month');
            };

            function onSelectNextMonthClick(e) {
                picker.modifyValue(1, 'month');
            };

            function onSelectedYearChanged(value, item) {
                if (!item?.data) return;
                picker.changeYear(item.data.value);
            };

            function onSelectedMonthChanged(value, item) {
                if (!item?.data) return;
                picker.changeMonth(item.data.value);
            };

        };

        function renderDates() {

            let controls = [].concat(
                // weekdays
                (picker.weekDays || []).map((weekDay, index) => {

                    return r.div('.weekday', [
                        r.span([weekDay])
                    ]);

                }),
                // days
                (picker.weeks || []).reduce((cells, week, weekIndex) => {
                    (week.days || []).forEach((day, dayIndex) => {

                        if (!day.value) {
                            cells.push(r.span('.day'));
                            return;
                        }

                        let css = '.day' + (day.disabled ? '.disabled' : ''),
                            props = day.disabled
                                ? {
                                    title: day.value.format(),
                                }
                                : {
                                    onclick: (e) => onDayClick(day.data, e),
                                    title: day.value.format(),
                                };

                        if (day.isSelected) css += '.active';
                        if (day.isToday) css += '.today';
                        if (day.isPrevMonth) css += '.prev-month';
                        if (day.isNextMonth) css += '.next-month';
                        if (day.isInRange) css += '.calendar-range';
                        if (day.isRangeStart) css += '.range-start';
                        if (day.isRangeEnd) css += '.range-end';
                        if (day.tooltip) {
                            css += '.tooltip';
                            props['data-tooltip'] = day.tooltip;
                        }

                        cells.push(
                            r.div(css, [
                                r.span({ css, props }, [day.text])
                            ])
                        );

                    });
                    return cells;
                }, [])
            );

            return r.grid({ css: '.calendar-days' }, controls);

            function onDayClick(data, e) {
                picker.modifyValueParts(data);
            };

        };

        function renderTimes() {

            const hours = (picker.hours || []).map((item) => {
                return { value: item.value, label: item.text, data: item };
            });
            const minutes = (picker.minutes || []).map((item) => {
                return { value: item.value, label: item.text, data: item }
            });

            return r.group({}, [
                r.iconButton('#first', { css: '.chip', props: { onclick: onSelectPrevHourClick } }),
                r.iconButton('#prior', { css: '.chip', props: { onclick: onSelectPrevMinuteClick } }),
                r.comboBox(selectedHour, { items: hours, emptyNotDisplayed: true, onValueSet: onSelectedHourChanged }),
                r.comboBox(selectedMinute, { items: minutes, emptyNotDisplayed: true, onValueSet: onSelectedMinuteChanged }),
                r.iconButton('#next', { css: '.chip', props: { onclick: onSelectNextMinuteClick } }),
                r.iconButton('#last', { css: '.chip', props: { onclick: onSelectNextHourClick } }),
            ]);

            function onSelectPrevHourClick(e) {
                picker.modifyValue(-1, 'hour');
            };

            function onSelectNextHourClick(e) {
                picker.modifyValue(1, 'hour');
            };

            function onSelectPrevMinuteClick(e) {
                picker.modifyValue(-picker.stepping, 'minute');
            };

            function onSelectNextMinuteClick(e) {
                picker.modifyValue(picker.stepping, 'minute');
            };

            function onSelectedMinuteChanged(value, item) {
                if (!item?.data) return;
                picker.changeMinute(item.data.value);
            };

            function onSelectedHourChanged(value, item) {
                if (!item?.data) return;
                picker.changeHour(item.data.value);
            };

        };

        function renderCloseButton() {

            return r.nav({}, [
                r.spacer(),
                r.iconButton('#close', {
                    css: '.circle.transparent',
                    props: {
                        onclick: (e) => {
                            e.stopPropagation();
                            setTimeout(() => {
                                document.body.click();
                            }, 0);
                        }
                    },
                }),
            ]);

        };

    };

}

function isPartEnabled(format, granularity) {

    if (typeof format != 'string')
        return false;

    if (typeof granularity !== 'string' || granularity.length > 1) {
        throw new TypeError('isPartEnabled expects a single character string parameter');
    }

    switch (granularity) {
        case 'y':
            return format.indexOf('Y') !== -1;
        case 'M':
            return format.indexOf('M') !== -1;
        case 'd':
            return format.toLowerCase().indexOf('d') !== -1;
        case 'h':
        case 'H':
            return format.toLowerCase().indexOf('h') !== -1;
        case 'm':
            return format.indexOf('m') !== -1;
        case 's':
            return format.indexOf('s') !== -1;
        default:
            return false;
    }
};

function pickFormat(dateRequired, timeRequired, formats) {

    return formats.find((format) => {

        if (dateRequired && !(isPartEnabled(format, 'y') && isPartEnabled(format, 'M') && isPartEnabled(format, 'd')))
            return false;
        // if (timeRequired && !(isPartEnabled(format, 'h') && isPartEnabled(format, 'm') && isPartEnabled(format, 's')))
        //     return false;
        if (timeRequired && !(isPartEnabled(format, 'h') && isPartEnabled(format, 'm')))
            return false;

        return true;

    });

};
